import {
  Badge,
  BodyText,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from "@/components/ui";
import type { ApiError } from "@/hooks/useApi";
import { getQuestionnaireCategory } from "@/network/useQuestionnaires";
import type {
  LifestyleQuestionnaireTypes,
  MentalHealthQuestionnaireTypes,
  QuestionnaireResponseData,
} from "@/types/questionnaires";
import { QuestionnaireResponse } from "@curai/js-fhir-tools";
import { useEffect, useMemo, useState } from "react";
import { useConfirmQuestionnaire } from "../../network";
import { formatAuthoredDate } from "../../utils";
import { QuestionnaireDetailDialogFooter } from "./QuestionnaireDetailDialogFooter";
import { QuestionnaireDetailDialogQuestions } from "./QuestionnaireDetailDialogQuestions";

interface QuestionnaireDetailDialogProps {
  selectedQuestionnaireType:
    | LifestyleQuestionnaireTypes
    | MentalHealthQuestionnaireTypes;
  allQuestionnaires: QuestionnaireResponseData[];
  patientId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const selectQuestionnaireResponse = (
  questionnaireId: string,
  allQuestionnaires: QuestionnaireResponseData[],
) => {
  return allQuestionnaires.find((q) => q.questionnaireId === questionnaireId);
};

export const QuestionnaireDetailDialog = ({
  selectedQuestionnaireType,
  allQuestionnaires,
  patientId,
  open,
  onOpenChange,
}: QuestionnaireDetailDialogProps) => {
  const [selectedQuestionnaire, setSelectedQuestionnaire] = useState<
    QuestionnaireResponseData | undefined
  >(undefined);

  // Dropdown options should be the questionnaires that have a response
  const actualQuestionnaireResponses = useMemo(
    () => allQuestionnaires.filter((q) => q.questionnaireResponse),
    [allQuestionnaires],
  );

  useEffect(() => {
    if (open) {
      const questionnaire = selectQuestionnaireResponse(
        selectedQuestionnaireType,
        actualQuestionnaireResponses,
      );
      setSelectedQuestionnaire(questionnaire);
    }
  }, [open, selectedQuestionnaireType, actualQuestionnaireResponses]);

  // Check if the questionnaire has been reviewed by looking for confirmation data
  const isReviewed = useMemo(() => {
    if (!selectedQuestionnaire?.questionnaireResponse) return false;
    const qr = new QuestionnaireResponse(
      selectedQuestionnaire.questionnaireResponse,
    );
    return (
      qr.getConfirmationActorName() !== undefined &&
      qr.getConfirmationOccurredDate() !== undefined
    );
  }, [selectedQuestionnaire]);

  const isCompleted = useMemo(() => {
    return selectedQuestionnaire?.questionnaireResponse?.status === "completed";
  }, [selectedQuestionnaire]);

  const isReviewable = useMemo(() => {
    return !isReviewed && isCompleted;
  }, [isReviewed, isCompleted]);

  const confirmQuestionnaire = useConfirmQuestionnaire({
    questionnaireResponseId:
      selectedQuestionnaire?.questionnaireResponse?.id || "dummy-id",
    patientId: patientId || "dummy-patient",
    questionnairesCategory: getQuestionnaireCategory(selectedQuestionnaireType),
    questionnaireType: selectedQuestionnaireType,
    onSuccess: () => {
      // The confirmation data will be updated via the query invalidation
      // No need for local state management
    },
    onError: (error: ApiError) => {
      const isIncomplete = error?.response?.status === 422;
      const errorMessage = isIncomplete
        ? "Confirmation failed: questionnaire incomplete"
        : "Confirmation failed";
      toast.error(errorMessage);
    },
  });

  if (!selectedQuestionnaire || !selectedQuestionnaire.questionnaireResponse) {
    return null;
  }

  const handleSelectChange = (
    value: LifestyleQuestionnaireTypes | MentalHealthQuestionnaireTypes,
  ) => {
    const newlySelected = selectQuestionnaireResponse(
      value,
      actualQuestionnaireResponses,
    );
    if (newlySelected) {
      setSelectedQuestionnaire(newlySelected);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogOverlay />
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex-shrink-0 space-y-1">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle asChild>
                <BodyText size="md" bold>
                  Questionnaires
                </BodyText>
              </DialogTitle>
              <DialogDescription className="hidden">
                Questionnaire Responses
              </DialogDescription>
            </div>
            {actualQuestionnaireResponses.length > 0 && (
              <div className="flex items-center justify-between pt-2">
                <Select
                  value={selectedQuestionnaire.questionnaireId}
                  onValueChange={handleSelectChange}
                >
                  <SelectTrigger className="w-auto">
                    <SelectValue placeholder="Select a questionnaire response" />
                  </SelectTrigger>
                  <SelectContent>
                    {actualQuestionnaireResponses.map((q) => (
                      <SelectItem
                        key={q.questionnaireId}
                        value={q.questionnaireId}
                      >
                        {q.questionnaireShortLabel} -{" "}
                        {formatAuthoredDate(q.questionnaireResponse?.authored)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {isReviewable && (
                  <div className="flex items-center gap-2">
                    <Badge variant="warning">
                      <BodyText textColor="warning" size="sm">
                        Not Reviewed
                      </BodyText>
                    </Badge>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => confirmQuestionnaire.mutate(undefined)}
                      isLoading={confirmQuestionnaire.isPending}
                      disabled={
                        confirmQuestionnaire.isPending ||
                        !selectedQuestionnaire?.questionnaireResponse?.id
                      }
                    >
                      Confirm Review
                    </Button>
                  </div>
                )}
              </div>
            )}
          </DialogHeader>
        </div>
        <QuestionnaireDetailDialogQuestions
          questionnaireResponse={selectedQuestionnaire.questionnaireResponse}
          totalQuestions={selectedQuestionnaire.questionCount ?? 0}
          questionnaireId={selectedQuestionnaire.questionnaireId}
        />
        <QuestionnaireDetailDialogFooter
          questionnaireResponse={selectedQuestionnaire.questionnaireResponse}
          responseScore={selectedQuestionnaire.responseScore}
          maxScore={selectedQuestionnaire.maxScore}
        />
      </DialogContent>
    </Dialog>
  );
};
